/**
 * Cross-service contracts.
 *
 * `SnapshotJob` is THE contract between:
 *   api → Cloudflare Queue → consumer → Cloud Tasks → runner
 * Pin field names and shapes here; downstream services must conform.
 */
import { z } from 'zod';

export const DeviceSchema = z.object({
  title: z.string(),
  type: z.string(),
  userAgent: z.string(),
  viewport: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  deviceScaleFactor: z.number().positive(),
  mobile: z.boolean(),
});

export const ThrottlingSchema = z.object({
  method: z.enum(['simulate', 'devtools', 'provided']),
  latencyMs: z.number().nonnegative(),
  downloadKbps: z.number().nonnegative(),
  uploadKbps: z.number().nonnegative(),
  /** Network preset name for telemetry (e.g. 'regular3g'); Lighthouse settings come from latency/kbps above. */
  preset: z.string().optional(),
});

export const CpuThrottlingSchema = z.object({
  /** Lighthouse `cpuSlowdownMultiplier`. 1 = no throttling. */
  multiplier: z.number().positive(),
});

/**
 * Bounded validator for caller-supplied `extra_headers` at API
 * boundaries (`POST /v1/audits`, monitor CRUD).
 *
 * SnapshotJob's own `extra_headers` field is more permissive
 * (`z.record(z.string())`) because we trust internal callers and don't
 * want a future protocol-level header (e.g. a tracing key the runner
 * adds itself) to trip validation. The bounded shape is enforced only
 * on the public ingress.
 *
 * Reserved headers blocked: anything that would let a caller break
 * the request shape (`Host`, `Content-Length`, `Connection`,
 * `Transfer-Encoding`) or leak credentials in ways the catalogue
 * isn't designed for (`Authorization`, `Proxy-Authorization`,
 * `Cookie`).
 */
const RESERVED_HEADER_NAMES = new Set([
  'host',
  'content-length',
  'connection',
  'transfer-encoding',
  'authorization',
  'proxy-authorization',
  'cookie',
]);

export const ExtraHeadersSchema = z.record(z.string().min(1).max(1024)).superRefine((val, ctx) => {
  const entries = Object.entries(val);
  if (entries.length > 20) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'at most 20 extra headers allowed',
    });
  }
  for (const [k] of entries) {
    if (k.length === 0 || k.length > 256) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [k],
        message: 'header name must be 1–256 chars',
      });
    }
    if (!/^[a-zA-Z0-9!#$%&'*+\-.^_`|~]+$/.test(k)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [k],
        message: 'header name contains illegal characters',
      });
    }
    if (RESERVED_HEADER_NAMES.has(k.toLowerCase())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [k],
        message: `header "${k}" is reserved and cannot be set`,
      });
    }
  }
});

/**
 * Bounded validator for caller-supplied `blocked_url_patterns` at API
 * boundaries (a variant's `blocked_partners` delta). Each entry is a Lighthouse
 * `blockedUrlPatterns` glob (substring match with `*` wildcards);
 * matching requests are aborted during the audit, letting a run measure
 * the page "without" a set of third-party partners.
 *
 * Like `ExtraHeadersSchema`, SnapshotJob's own field (below) is looser —
 * the strict shape is enforced only on public ingress.
 */
export const BlockedUrlPatternsSchema = z
  .array(z.string().min(1).max(512))
  .max(100, 'at most 100 blocked URL patterns allowed');

export const SnapshotJobSchema = z.object({
  /** Identifiers — all prefixed nanoids from packages/shared/ids.ts */
  snapshot_uuid: z.string().startsWith('ss-'),
  snapshot_group_uuid: z.string().startsWith('ssg-'),
  org_uuid: z.string().startsWith('org-'),
  team_uuid: z.string().startsWith('team-').optional(),
  site_uuid: z.string().startsWith('site-'),
  page_uuid: z.string().startsWith('pg-'),
  device_profile_uuid: z.string().startsWith('dp-'),
  region_uuid: z.string().startsWith('rg-'),

  /** Absolute URL to audit. */
  url: z.string().url(),

  /** Execution parameters. */
  region: z.string(), // e.g. "us-central1"
  locale: z.string().default('en-US'),
  device: DeviceSchema,
  throttling: ThrottlingSchema,
  cpu_throttling: CpuThrottlingSchema,

  /**
   * Optional per-job override for the callback URL. Reserved — the
   * runner today reads `WEBHOOKS_URL` from its env. Future per-tenant
   * or per-region routing will honour this field when present.
   */
  callback_url: z.string().url().optional(),

  /**
   * Optional custom HTTP headers sent with every request the runner
   * makes against the audit URL. Used by customers whose sites sit
   * behind a CDN/WAF (Cloudflare, Akamai, etc.) — they configure a
   * bypass rule keyed off one of these headers so the runner gets
   * through bot protection.
   *
   * Bounded by `ExtraHeadersSchema` below: reserved headers rejected,
   * 20 entries max, key ≤ 256 chars, value ≤ 1024 chars. Values are
   * treated as sensitive — mask in logs.
   */
  extra_headers: z.record(z.string()).optional(),

  /**
   * Optional URL glob patterns aborted during the run (Lighthouse
   * `blockedUrlPatterns`). Populated by a variant's `blocked_partners`
   * delta to measure the page without a set of third-party requests. Looser here
   * than the API-boundary `BlockedUrlPatternsSchema`; the strict shape is
   * validated on ingress.
   */
  blocked_url_patterns: z.array(z.string()).optional(),

  /** Correlation and bookkeeping. */
  request_id: z.string().optional(),
  attempt: z.number().int().min(1).default(1),
  enqueued_at: z.string().datetime().optional(),
});

export type SnapshotJob = z.infer<typeof SnapshotJobSchema>;
export type Device = z.infer<typeof DeviceSchema>;
export type Throttling = z.infer<typeof ThrottlingSchema>;
export type CpuThrottling = z.infer<typeof CpuThrottlingSchema>;
